import { useEffect, useRef } from 'react';

import useCustomRouter from '@/hooks/useCustomRouter';
import { DiagnosisCategoryType, EDiagnosisStep, TreatmentRequest } from '@/models/diagnosis';
import { ROUTES } from '@/types/common';
import { addQueryString } from '@/utils/url';
import { useToast } from 'doctornow-design-system';

interface UseStepValidationParams {
  step: EDiagnosisStep;
  treatmentRequest: TreatmentRequest;
  isRequiredDataFetched: boolean;
}

const useStepValidation = ({ step, treatmentRequest, isRequiredDataFetched }: UseStepValidationParams) => {
  const { router } = useCustomRouter();
  const { toast } = useToast();
  const hasValidatedRef = useRef(false);
  const currentStepRef = useRef(step);

  useEffect(() => {
    if (currentStepRef.current !== step) {
      hasValidatedRef.current = false;
      currentStepRef.current = step;
    }

    if (!isRequiredDataFetched || hasValidatedRef.current) {
      return;
    }

    const isDiagnosisStepValid = (): boolean => {
      const form = treatmentRequest.form;
      return !!(
        form.categoryId &&
        form.categoryType &&
        form.bookingTargetDate &&
        form.startTimeSlotValue !== -1 && // -1은 미선택 초깃값
        form.endTimeSlotValue !== -1
      );
    };

    const isDoctorStepValid = (): boolean => {
      return isDiagnosisStepValid();
    };

    const isPaymentStepValid = (): boolean => {
      return isDiagnosisStepValid() && !!treatmentRequest.form.doctorId;
    };

    const redirectToFirstStep = () => {
      toast({
        text: '진료 신청서를 다시 작성해 주세요.',
        direction: 'bottom',
      });

      router.replace(
        ROUTES.TELEMEDICINE.REQUEST.path +
          addQueryString({
            step: EDiagnosisStep.Diagnosis,
            type: router.query.type as DiagnosisCategoryType,
            categoryId: router.query.categoryId as string,
          })
      );
    };

    if (step === EDiagnosisStep.Doctor && !isDoctorStepValid()) {
      redirectToFirstStep();
      return;
    }

    if (step === EDiagnosisStep.Payment && !isPaymentStepValid()) {
      redirectToFirstStep();
      return;
    }

    hasValidatedRef.current = true;
  }, [step, treatmentRequest, isRequiredDataFetched, router, toast]);
};

export default useStepValidation;
